import { ClientRegistrationState } from '@common/model/client/registration/registrationState';
import { ClientType } from '@common/model/client/clientType';
import { shallowEqual } from 'react-redux';
import _uniq from 'lodash/uniq';
import { StepName } from './stepNames';
import { FieldList, IRegistrationStepFactory, RegistrationStep } from './step';
import { SpecialistFactory } from './stepFactories/specialistFactory';
import { EmployerFactory } from './stepFactories/employerFactory';
import { commonFactory } from './stepFactories/commonFactory';

export type FlowStep = {
  totalSteps: number;
  currentStepNumber: number;
  step: RegistrationStep | null;
};

const getStepFactory = (state: ClientRegistrationState): IRegistrationStepFactory => {
  switch (state.clientType) {
    case ClientType.Employer:
      return new EmployerFactory();
    case ClientType.Specialist:
      return new SpecialistFactory();
    default:
      return commonFactory;
  }
};

// eslint-disable-next-line func-style
export function* generateRegistrationFlow(
  initialState: ClientRegistrationState,
  initialRemainingFields: FieldList,
): Generator<FlowStep, FlowStep, { state: ClientRegistrationState; fields: FieldList } | undefined> {
  let stepFactory = getStepFactory(initialState);
  let clientType = initialState.clientType;
  let remainingFields = initialRemainingFields;
  let remainingStepNames = stepFactory.getStepNames(remainingFields);
  const totalSteps = new Set(remainingStepNames);
  let stepName: StepName | undefined;
  let step: RegistrationStep | null = null;
  let currentStepNumber = 0;

  while (remainingStepNames.length) {
    stepName = remainingStepNames.shift();
    if (!stepName) {
      continue;
    }
    step = stepFactory.createStep(stepName, remainingFields);
    currentStepNumber++;

    const refinedData = yield { totalSteps: totalSteps.size, step, currentStepNumber };

    if (refinedData) {
      const updatedClientType = clientType !== refinedData.state.clientType;
      const updatedFields = !shallowEqual(refinedData.fields || [], remainingFields);
      if (updatedClientType) {
        clientType = refinedData.state.clientType;
        stepFactory = getStepFactory(refinedData.state);
        remainingStepNames = stepFactory.getStepNames(updatedFields ? refinedData.fields : remainingFields);
      }
      if (updatedFields) {
        remainingFields = _uniq(
          remainingFields.filter((fieldName) => !Object.hasOwn(refinedData.state, fieldName)).concat(refinedData.fields),
        );
        remainingStepNames = stepFactory.getStepNames(remainingFields);
      }
      remainingStepNames.map((n) => totalSteps.add(n));
    }
  }

  return { totalSteps: totalSteps.size, step, currentStepNumber };
}
